import ApiService from "src/util/api.service";
import { getLanguageHeaders } from "./commonLanguageHeader";
import { ServicesUrlEndpoints } from "./servicesEndpointConstant";

export interface CustomerLimitAssignment {
  code: string;
  customerMaxLimit: number;
}

export interface CustomerLimitSummary {
  code: string;
  value: number;
  maxLimit: number | null;
  customerMaxLimit: number | null;
  remaining: number | null;
  resetAt?: string | number | null;
}

export type CustomerLimitsPayload = CustomerLimitAssignment[];

const readLimitNumber = (value: unknown): number | null => {
  if (typeof value !== "number" && typeof value !== "string") return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const normalizeCustomerLimitsResponse = (value: unknown): CustomerLimitSummary[] => {
  const values = Array.isArray(value) ? value : value ? [value] : [];

  return values.flatMap((item) => {
    if (!item || typeof item !== "object") return [];
    const record = item as Record<string, unknown>;
    if (typeof record.code !== "string") return [];

    return [
      {
        code: record.code,
        value: readLimitNumber(record.value) ?? 0,
        maxLimit: readLimitNumber(record.maxLimit),
        customerMaxLimit: readLimitNumber(record.customerMaxLimit),
        remaining: readLimitNumber(record.remaining),
        resetAt:
          typeof record.resetAt === "string" || typeof record.resetAt === "number"
            ? record.resetAt
            : null,
      },
    ];
  });
};

const routeForCustomer = (route: string, clientId: string | number) =>
  route.replace("{clientId}", String(clientId));

export const getCustomerLimits = (clientId: string | number, language?: string) =>
  ApiService.get<{ response: CustomerLimitSummary[] }>(
    routeForCustomer(ServicesUrlEndpoints.CUSTOMER.LIMITS, clientId),
    {
      ...getLanguageHeaders(language),
    }
  );

export const getCustomerLimit = (clientId: string | number, code: string, language?: string) =>
  ApiService.get<{ response: CustomerLimitSummary }>(
    routeForCustomer(ServicesUrlEndpoints.CUSTOMER.LIMIT_BY_CODE, clientId).replace(
      "{code}",
      encodeURIComponent(code)
    ),
    {
      ...getLanguageHeaders(language),
    }
  );

export const updateCustomerLimits = (
  clientId: string | number,
  payload: CustomerLimitsPayload,
  language?: string
) =>
  ApiService.patch<{ response: CustomerLimitSummary[] }>(
    routeForCustomer(ServicesUrlEndpoints.CUSTOMER.LIMITS, clientId),
    payload,
    {
      ...getLanguageHeaders(language),
    }
  );

export const resetCustomerLimit = (clientId: string | number, code: string, language?: string) =>
  ApiService.post<unknown>(
    routeForCustomer(ServicesUrlEndpoints.CUSTOMER.RESET_LIMIT, clientId).replace(
      "{code}",
      encodeURIComponent(code)
    ),
    undefined,
    {
      ...getLanguageHeaders(language),
    }
  );
